import React, { useRef, useState } from "react";
import styled from "styled-components";
import {
    PROJECT_DESCRIPTION_MAX_LENGTH,
    PROJECT_TITLE_MAX_LENGTH,
    PROJECT_TITLE_MIN_LENGTH,
} from "@lifeat/shared/projects/ProjectsConstants";
import { Project } from "@lifeat/shared/projects/ProjectsTypes";
import { addDaysToDate, calculateDaysBetweenDates, getDateIsoString } from "@lifeat/shared/util/DateUtil";
import { AmplitudeService } from "@analytics/Amplitude/AmplitudeService";
import { LifeAtButtonV2 } from "@common/LifeAtButtonV2/LifeAtButtonV2";
import { DateTimeInput } from "@common/LifeAtDateTimeInput/LifeAtDateTimeInput";
import { DropdownMenuComponents } from "@common/LifeAtDropdownMenuV2/DropdownMenuComponents";
import Tab from "@common/LifeAtTabs/Tab";
import Tabs from "@common/LifeAtTabs/Tabs";
import { LifeAtBodySecondary } from "@common/LifeAtText/LifeAtText";
import LifeAtMultiLineTextInput from "@common/LifeAtTextInput/LifeAtMultiLineTextInput";
import { useDebouncedInput } from "@common/LifeAtTextInput/useDebouncedInput";
import { useToastListContext } from "@contexts/ToastListContext";
import { UserContextType, useUserContext } from "@contexts/UserContext";
import Arrow from "@icons/Arrow-Left-Icon.svg";
import ChevronLeftIcon from "@icons/ChevronLeftArrow.svg";
import MoreActionsIcon from "@icons/Menu-TripleVerticalDots-Icon.svg";
import { FontSize } from "@themes/Fonts";
import { TaskListContext } from "../../task-management/TaskListContext";
import TaskList from "../../task-management/components/TaskList";
import { DropdownItem } from "../../task-management/components/TaskTagDropdownComponents";
import { SelectPlannerInvoicesViewEvent } from "../../task-management/planner/AmplitudePlannerEvents";
import useTaskData from "../../task-management/useTaskData";
import {
    UpdateProjectDescriptionEvent,
    UpdateProjectEndDateEvent,
    UpdateProjectStartDateEvent,
    UpdateProjectTitleEvent,
} from "../AmplitudeProjectsEvents";
import { DELETE_PROJECT_CONFIRMATION } from "../ProjectConstants";
import { ProjectDetailsContent } from "../ProjectTypes";
import { ProjectsService } from "../ProjectsService";
import ProjectColorPicker from "./ProjectColorPicker";
import ProjectStatusPicker from "./ProjectStatusPicker";
import ProjectsTaskProgressBar from "./ProjectsTaskProgressBar";

const ProjectDetailsWrapper = styled.div`
    height: 100%;
    width: 100%;

    display: flex;
    flex-direction: column;
`;

const ProjectDetailsTopHalf = styled.div`
    background-color: ${(props) => props.theme.backgroundPrimary};
    border-bottom: 1px solid ${(props) => props.theme.borderPrimary};
`;

const StyledChevronLeftIcon = styled(ChevronLeftIcon)`
    cursor: pointer;
    fill: ${(props) => props.theme.iconColor};
`;

const HeaderRow = styled.div`
    display: flex;
`;

const ProjectTitleRow = styled.div`
    width: 100%;
    border-radius: 3px;

    * > * {
        background-color: unset;
    }

    &:hover {
        background-color: ${(props) => props.theme.backgroundSpecial};
    }
`;

const ProjectDetailsInfoWrapper = styled.div`
    display: flex;
    flex-direction: column;
    padding: 0 15px 15px 15px;
    max-width: 600px;
    gap: 15px;
`;

const ProjectDetailsContentWrapper = styled.div`
    display: flex;
    flex-direction: column;
    padding: 0 8px;
    gap: 15px;
`;

const ProjectSelectorsRow = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    gap: 10px;
`;

const StyledMoreActionsIcon = styled(MoreActionsIcon)`
    fill: ${(props) => props.theme.iconColor};
    cursor: pointer;
    transform: rotate(90deg);
`;

const StyledMoreActionsDropdownTrigger = styled(DropdownMenuComponents.Trigger)`
    display: flex;
    padding: 3px 8px;
    cursor: pointer;
`;

const ProjectDescriptionInput = styled(LifeAtMultiLineTextInput)`
    width: 100%;
`;

const TaskListContainer = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    padding-top: 20px;
`;

const StyledProjectsTaskProgressBar = styled(ProjectsTaskProgressBar)`
    padding: 0 20px;
    max-width: 500px;
`;

const InvoiceContainer = styled.div`
    height: 796px;
`;

const StyledIframe = styled.iframe`
    height: inherit;
    width: 100%;
    border: none;
`;

const TabsContainer = styled.div`
    padding: 0 15px;
`;

const ProjectTimelineField = styled.div`
    display: flex;
    align-items: center;
    gap: 15px;
`;

const ProjectTimelineInput = styled.div`
    display: flex;
    align-items: center;
    gap: 5px;
`;

const ArrowIcon = styled(Arrow)`
    fill: ${(props) => props.theme.textColorPrimary};
    transform: rotate(180deg);
`;

const DEBOUNCE_DELAY_MS = 1000;

const BACK_BUTTON_LABEL = "Projects";
const TIMELINE_INPUT_LABEL = "Timeline";
const PROJECT_DESCRIPTION_LABEL = "Description";
const PROJECT_DESCRIPTION_PLACEHOLDER = "Add details about the goals, plans, and execution of your project";

const DELETE_PROJECT_BTN_LABEL = "Delete";

const TASKS_TAB_LABEL = "Tasks";
const INVOICING_TAB_LABEL = "Invoicing";
const INVOICING_LINK = "https://www.lifeat.com/invoice";

interface ProjectDetailsProps {
    projectId: string;
    project: Project;
    onClose: () => void;
}
function ProjectDetails(props: ProjectDetailsProps) {
    const { projectId, project, onClose } = props;

    const amplitudeService = AmplitudeService.getInstance();

    const { user }: UserContextType = useUserContext();
    const { addToastNotification } = useToastListContext();
    const taskData = useTaskData({
        uid: user?.uid,
        selectedProjectId: projectId,
    });

    const [activeProjectDetailsTab, setActiveProjectDetailsTab] = useState<ProjectDetailsContent>("tasks");

    const [titleInputVal, setTitleInputValAndDebounce] = useDebouncedInput(
        project.title,
        (updatedTitle: string) => {
            ProjectsService.updateProjectTitle(projectId, updatedTitle).then(() => {
                amplitudeService.logEvent(new UpdateProjectTitleEvent());
            });
        },
        DEBOUNCE_DELAY_MS,
    );

    const [descriptionInputVal, setDescriptionInputValAndDebounce] = useDebouncedInput(
        props.project.description || "",
        (updatedDescription: string) => {
            ProjectsService.updateProjectDescription(projectId, updatedDescription).then(() => {
                amplitudeService.logEvent(new UpdateProjectDescriptionEvent());
            });
        },
        DEBOUNCE_DELAY_MS,
    );

    const startDateInputRef = useRef<null | HTMLInputElement>(null);
    const [startDateInputVal, setStartDateInputValAndDebounce] = useDebouncedInput(
        props.project.startDate || "",
        (updatedStartDate: string) => {
            const newStartDate = updatedStartDate === "" ? null : updatedStartDate;
            ProjectsService.updateProjectStartDate(projectId, newStartDate)
                .then(() => {
                    amplitudeService.logEvent(new UpdateProjectStartDateEvent());
                })
                .catch((error) => {
                    addToastNotification("Error updating project timeline", error.message);
                });
        },
        DEBOUNCE_DELAY_MS,
    );

    const endDateInputRef = useRef<null | HTMLInputElement>(null);
    const [endDateInputVal, setEndDateInputValAndDebounce] = useDebouncedInput(
        props.project.endDate || "",
        (updatedEndDate: string) => {
            const newEndDate = updatedEndDate === "" ? null : updatedEndDate;
            ProjectsService.updateProjectEndDate(projectId, newEndDate)
                .then(() => {
                    amplitudeService.logEvent(new UpdateProjectEndDateEvent());
                })
                .catch((error) => {
                    addToastNotification("Error updating project timeline", error.message);
                });
        },
        DEBOUNCE_DELAY_MS,
    );

    const handleStartDateChange = (startDate: string) => {
        // If the start date is after the end date, we should update the end date
        // to be the same number of days after the new start date
        if (new Date(endDateInputVal) < new Date(startDate)) {
            const oldDateDiff = calculateDaysBetweenDates(new Date(endDateInputVal), new Date(startDateInputVal));
            const newEndDate = addDaysToDate(new Date(startDate), oldDateDiff);
            setEndDateInputValAndDebounce(getDateIsoString(newEndDate));
        }
        setStartDateInputValAndDebounce(startDate);
    };

    const handleEndDateChange = (endDate: string) => {
        // If the end date is before the start date, we should update the start date
        // to be the same number of days before the new end date
        if (new Date(endDate) < new Date(startDateInputVal)) {
            const oldDateDiff = calculateDaysBetweenDates(new Date(endDateInputVal), new Date(startDateInputVal));
            const newStartDate = addDaysToDate(new Date(endDate), -oldDateDiff);
            setStartDateInputValAndDebounce(getDateIsoString(newStartDate));
        }
        setEndDateInputValAndDebounce(endDate);
    };

    const prefillStartDate = () => {
        if (startDateInputVal === "") {
            const currentDate = new Date();
            setStartDateInputValAndDebounce(getDateIsoString(currentDate));
        }
        startDateInputRef.current?.showPicker();
    };
    const prefillEndDate = () => {
        if (endDateInputVal === "") {
            const currentDate = new Date();
            currentDate.setDate(currentDate.getDate() + 1);
            setEndDateInputValAndDebounce(getDateIsoString(currentDate));
        }
        endDateInputRef.current?.showPicker();
    };

    const handleDeleteProject = () => {
        if (confirm(DELETE_PROJECT_CONFIRMATION)) {
            onClose();
            ProjectsService.deleteProject(projectId).catch((error) => {
                addToastNotification("Failed to delete project, please try again", error.message);
            });
        }
    };

    const handleTasksViewSelect = () => {
        setActiveProjectDetailsTab("tasks");
    };

    const handleInvoiceViewSelect = () => {
        setActiveProjectDetailsTab("invoicing");
        amplitudeService.logEvent(new SelectPlannerInvoicesViewEvent());
    };

    const projectActionsMenu = (
        <DropdownMenuComponents.Root>
            <StyledMoreActionsDropdownTrigger onClick={(e) => e.stopPropagation()}>
                <StyledMoreActionsIcon />
            </StyledMoreActionsDropdownTrigger>
            <DropdownMenuComponents.Content>
                <DropdownItem onClick={handleDeleteProject}>{DELETE_PROJECT_BTN_LABEL}</DropdownItem>
            </DropdownMenuComponents.Content>
        </DropdownMenuComponents.Root>
    );

    const projectsTaskList = (
        <TaskListContainer>
            <StyledProjectsTaskProgressBar projectColor={project.color} taskList={taskData?.taskItems$.value} />

            <TaskListContext.Provider value={taskData}>
                <TaskList
                    taskList$={taskData?.taskItems$}
                    amplitudeEventSource={"projects-details"}
                    showProgressBar={false}
                    updateTargetOnTaskMove={"task-project"}
                />
            </TaskListContext.Provider>
        </TaskListContainer>
    );

    const invoicingView = (
        <InvoiceContainer>
            <StyledIframe src={INVOICING_LINK} />
        </InvoiceContainer>
    );

    return (
        <ProjectDetailsWrapper className={`project-details-wrapper`}>
            <ProjectDetailsTopHalf>
                <HeaderRow>
                    <LifeAtButtonV2 variant={"ghost"} size={"small"} onClick={onClose} disableHover={true}>
                        <StyledChevronLeftIcon />
                        {BACK_BUTTON_LABEL}
                    </LifeAtButtonV2>
                </HeaderRow>
                <ProjectDetailsInfoWrapper>
                    <ProjectTitleRow>
                        <LifeAtMultiLineTextInput
                            textValue={titleInputVal}
                            onInputChange={setTitleInputValAndDebounce}
                            minHeightPx={21}
                            maxHeightPx={200}
                            maxCharacterCount={PROJECT_TITLE_MAX_LENGTH}
                            errorMessage={
                                titleInputVal.length >= PROJECT_TITLE_MAX_LENGTH
                                    ? `Project title must be between ${PROJECT_TITLE_MIN_LENGTH} and ${PROJECT_TITLE_MAX_LENGTH} characters`
                                    : undefined
                            }
                            fontSizeCss={FontSize.large}
                        />
                    </ProjectTitleRow>
                    <ProjectDetailsContentWrapper>
                        <ProjectSelectorsRow>
                            <ProjectColorPicker projectId={projectId} color={project.color} />
                            <ProjectStatusPicker projectId={projectId} status={project.status} />
                            {projectActionsMenu}
                        </ProjectSelectorsRow>
                        <ProjectTimelineField>
                            <LifeAtBodySecondary>{TIMELINE_INPUT_LABEL}</LifeAtBodySecondary>
                            <ProjectTimelineInput>
                                <DateTimeInput
                                    ref={startDateInputRef}
                                    id={"project-start-date"}
                                    type={startDateInputVal !== "" ? "date" : "text"}
                                    value={startDateInputVal}
                                    onChange={(e) => handleStartDateChange(e.target.value)}
                                    onFocus={() => prefillStartDate()}
                                    onClick={() => prefillStartDate()}
                                    placeholder={"00/00/00"}
                                    widthPx={95}
                                />
                                <ArrowIcon />
                                <DateTimeInput
                                    ref={endDateInputRef}
                                    id={"project-end-date"}
                                    type={endDateInputVal !== "" ? "date" : "text"}
                                    value={endDateInputVal}
                                    onFocus={() => prefillEndDate()}
                                    onClick={() => prefillEndDate()}
                                    onChange={(e) => handleEndDateChange(e.target.value)}
                                    placeholder={"00/00/00"}
                                    widthPx={95}
                                />
                            </ProjectTimelineInput>
                        </ProjectTimelineField>

                        <ProjectDescriptionInput
                            textValue={descriptionInputVal}
                            onInputChange={setDescriptionInputValAndDebounce}
                            minHeightPx={30}
                            maxHeightPx={200}
                            maxCharacterCount={PROJECT_DESCRIPTION_MAX_LENGTH}
                            placeholder={PROJECT_DESCRIPTION_PLACEHOLDER}
                            errorMessage={
                                descriptionInputVal.length >= PROJECT_DESCRIPTION_MAX_LENGTH
                                    ? `Description cannot exceed ${PROJECT_DESCRIPTION_MAX_LENGTH} characters`
                                    : undefined
                            }
                            showBorder={true}
                            label={PROJECT_DESCRIPTION_LABEL}
                        />
                    </ProjectDetailsContentWrapper>
                </ProjectDetailsInfoWrapper>

                <TabsContainer>
                    <Tabs>
                        <Tab
                            key={`task-tab`}
                            label={TASKS_TAB_LABEL}
                            isActiveTab={activeProjectDetailsTab === "tasks"}
                            handleSelect={handleTasksViewSelect}
                            underlineColor={"red"}
                        />
                        <Tab
                            key={`invoicing-tab`}
                            label={INVOICING_TAB_LABEL}
                            isActiveTab={activeProjectDetailsTab === "invoicing"}
                            handleSelect={handleInvoiceViewSelect}
                            underlineColor={"red"}
                        />
                    </Tabs>
                </TabsContainer>
            </ProjectDetailsTopHalf>

            {activeProjectDetailsTab === "tasks" ? projectsTaskList : invoicingView}
        </ProjectDetailsWrapper>
    );
}

export default ProjectDetails;
